/* import __COLOCATED_TEMPLATE__ from './dynamic-contents-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type DynamicContent from 'embercom/models/articles/dynamic-content';

type DrawerView = 'list' | 'create' | 'edit';

interface EditorActions {
  save: () => Promise<void>;
}

interface Args {
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

export default class DynamicContentsDrawer extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare helpCenterService: $TSFixMe;

  @tracked drawerView: DrawerView = 'list';
  @tracked editingRecord: DynamicContent | null = null;
  @tracked dynamicContents: DynamicContent[] = [];
  @tracked isLoading = false;
  @tracked isSaving = false;

  private editorActions: EditorActions | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.loadDynamicContents();
  }

  get headerTitle(): string {
    if (this.drawerView === 'create') {
      return this.intl.t('knowledge-hub.dynamic-contents.editor.create-title');
    }
    if (this.drawerView === 'edit') {
      return this.intl.t('knowledge-hub.dynamic-contents.editor.edit-title');
    }
    return this.intl.t('knowledge-hub.dynamic-contents.page-title');
  }

  @action
  async loadDynamicContents() {
    this.isLoading = true;
    try {
      let results = await this.store.findAll('articles/dynamic-content');
      this.dynamicContents = results.toArray();
    } finally {
      this.isLoading = false;
    }
  }

  @action
  showCreateView() {
    let record = this.store.createRecord('articles/dynamic-content', {
      name: '',
      contentType: 'block',
      defaultLocale:
        this.helpCenterService.defaultSite?.defaultLocale?.localeId ?? this.appService.app.locale,
    });
    this.editingRecord = record;
    this.drawerView = 'create';
  }

  @action
  showEditView(dynamicContent: DynamicContent) {
    this.editingRecord = dynamicContent;
    this.drawerView = 'edit';
  }

  @action
  async backToList() {
    this.cleanupEditingRecord();
    this.editorActions = null;
    this.drawerView = 'list';
    await this.loadDynamicContents();
  }

  @action
  async handleSaveSuccess() {
    this.editingRecord = null;
    this.editorActions = null;
    this.drawerView = 'list';
    await this.loadDynamicContents();
  }

  @action
  handleClose() {
    if (this.drawerView !== 'list') {
      this.cleanupEditingRecord();
    }
    this.editorActions = null;
    this.drawerView = 'list';
    this.editingRecord = null;
    this.args.onClose();
  }

  @action
  handleDelete(dynamicContent: DynamicContent) {
    this.dynamicContents = this.dynamicContents.filter((dc) => dc !== dynamicContent);
  }

  @action
  cancelEdit() {
    this.cleanupEditingRecord();
    this.editorActions = null;
    this.drawerView = 'list';
    this.loadDynamicContents();
  }

  @action
  onEditorReady(actions: EditorActions) {
    this.editorActions = actions;
  }

  @action
  async triggerEditorSave() {
    if (!this.editorActions) {
      return;
    }
    this.isSaving = true;
    try {
      await this.editorActions.save();
    } finally {
      this.isSaving = false;
    }
  }

  private cleanupEditingRecord() {
    if (!this.editingRecord) {
      return;
    }
    if (!this.editingRecord.isDestroyed && !this.editingRecord.isDestroying) {
      if (this.editingRecord.get('isNew')) {
        this.editingRecord.deleteRecord();
      } else {
        this.editingRecord.rollbackAttributes();
      }
    }
    this.editingRecord = null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::DynamicContents::DynamicContentsDrawer': typeof DynamicContentsDrawer;
    'knowledge-hub/dynamic-contents/dynamic-contents-drawer': typeof DynamicContentsDrawer;
  }
}
